"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault").default;
var _interopRequireWildcard = require("@babel/runtime/helpers/interopRequireWildcard").default;
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.FormField = void 0;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _objectWithoutProperties2 = _interopRequireDefault(require("@babel/runtime/helpers/objectWithoutProperties"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _react = _interopRequireWildcard(require("react"));
var _identifier = require("../../util/identifier");
var _FormField = require("./FormField.context");
var _FormField2 = require("./FormField.styles");
var _excluded = ["children", "control", "controlId", "description", "disabled", "error", "label", "optional", "readOnly", "required"];
/**
 * @related [`FormHelp`, `Label`]
 * @since 4.0.0
 */
var FormField = /*#__PURE__*/(0, _react.forwardRef)(function (_ref, forwardedRef) {
  var children = _ref.children,
    control = _ref.control,
    controlId = _ref.controlId,
    description = _ref.description,
    disabled = _ref.disabled,
    error = _ref.error,
    label = _ref.label,
    optional = _ref.optional,
    readOnly = _ref.readOnly,
    required = _ref.required,
    rest = (0, _objectWithoutProperties2.default)(_ref, _excluded);
  // parentContext comes from components that extend this, e.g. FieldSet
  var parentContext = (0, _react.useContext)(_FormField.FormFieldContext);
  // defaultId is used as the id for the control and the htmlFor for the label
  var defaultId = (0, _react.useRef)((0, _identifier.identifier)()).current;
  // descriptionId is used for a field description, e.g. FormHelp
  var descriptionId = (0, _react.useRef)((0, _identifier.identifier)()).current;
  // labelId is only necessary when there are multiple labels, e.g. AdornedInput
  var labelId = (0, _react.useRef)((0, _identifier.identifier)()).current;
  var context = {
    controlId: typeof controlId !== 'undefined' ? controlId : defaultId,
    labelId: labelId,
    required: required
  };
  if (description) {
    // Use descriptionId from the parent context if it exists
    context.descriptionId = parentContext.descriptionId || descriptionId;
  }
  if (typeof optional !== 'undefined') {
    context.optional = optional;
  }
  if (typeof disabled !== 'undefined') {
    context.disabled = disabled;
  }
  if (typeof error !== 'undefined') {
    context.error = error;
  }
  if (typeof readOnly !== 'undefined') {
    context.readOnly = readOnly;
  }
  return /*#__PURE__*/_react.default.createElement(_FormField2.StyledFormField, (0, _extends2.default)({
    ref: forwardedRef
  }, rest), /*#__PURE__*/_react.default.createElement(_FormField.FormFieldContext.Provider, {
    value: context
  }, label, control, children, description));
});
exports.FormField = FormField;
FormField.displayName = 'FormField';
FormField.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * In general you will want to use the `control`, `description`, and `label` props instead
   * of `children`, but you can use `children` if you need a specialized layout. When using
   * `children`, however, not all ARIA properties can automatically be associated, so you must
   * verify that your specialized layout follow accessibility best practices.
   */
  children: _propTypes.default.node,
  /**
   * A single control node, e.g. Input, Select, Textarea.
   * When composing several different controls in one field, use a FieldSet.
   */
  control: _propTypes.default.node,
  /**
   * Identifier used for form controls and labels. This will be used as the `id` by
   * form controls and the `htmlFor` by Label components.
   *
   * If no `controlId` is specified, one will automatically be generated.
   */
  controlId: _propTypes.default.string,
  /**
   * A node that describes the form field, typically a FormHelp.
   * This will automatically associate the FormHelp with the field control using
   * [`aria-describedby`](https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/ARIA_Techniques/Using_the_aria-describedby_attribute).
   */
  description: _propTypes.default.node,
  /**
   * A node that labels the form field, typically a Label component.
   */
  label: _propTypes.default.node,
  /**
   * Indicates the form field is optional in the label.
   */
  optional: _propTypes.default.bool,
  /**
   * Indicates the form field is required. Inherited by the nested form control and label.
   */
  required: _propTypes.default.bool,
  /**
   * Displays the form field in a disabled state. Inherited by the nested form control
   * and label.
   *
   * @since 8.9.0
   */
  disabled: _propTypes.default.bool,
  /**
   * Displays the form field in an error state. Inherited by the nested form control.
   *
   * @since 8.9.0
   */
  error: _propTypes.default.bool,
  /**
   * Displays the form field in a read-only state. Inherited by the nested form control.
   *
   * @since 8.27.0
   */
  readOnly: _propTypes.default.bool
} : {};